import React, { useContext, useMemo, useState } from "react";
import { CartContext } from "./CartContext";
import "../css/checkout.css";
import productData from "../Datas/productData";
import { Link } from "react-router-dom";
import { checkoutCountries, formatMoney, STORE } from "../config/store";
import { buildPaymentUrl, makeOrderNumber } from "../utils/payment";

const initialForm = {
  email: "",
  country: "HK",
  firstName: "",
  lastName: "",
  address: "",
  address2: "",
  city: "",
  region: "",
  postalCode: "",
};

function CheckOut() {
  const { cartItems } = useContext(CartContext);
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const lineItems = useMemo(() => cartItems.map((item) => ({
    ...item,
    product: productData.find((product) => product.id === item.id),
  })).filter((item) => item.product), [cartItems]);

  const subtotal = lineItems.reduce((sum, item) => sum + Number(item.product.price) * item.quantity, 0);
  const total = subtotal + STORE.shippingFee;

  const updateField = ({ target: { name, value } }) => {
    setForm((current) => ({ ...current, [name]: value }));
    setErrors((current) => ({ ...current, [name]: "" }));
  };

  const validate = () => {
    const next = {};
    if (!/^\S+@\S+\.\S+$/.test(form.email.trim())) next.email = "Enter a valid email address.";
    if (!form.country) next.country = "Select a country or region.";
    if (!form.firstName.trim()) next.firstName = "Enter a first name.";
    if (!form.lastName.trim()) next.lastName = "Enter a last name.";
    if (!form.address.trim()) next.address = "Enter a street address.";
    if (!form.city.trim()) next.city = "Enter a city.";
    if (form.country !== "HK" && !form.postalCode.trim()) next.postalCode = "Enter a postal code.";
    if (!lineItems.length) next.cart = "Your cart is empty.";
    const unavailable = lineItems.find((item) => item.product.inventory < item.quantity);
    if (unavailable) next.cart = `${unavailable.product.name} no longer has the requested quantity available.`;
    return next;
  };

  const handlePayNow = (event) => {
    event.preventDefault();
    const nextErrors = validate();
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;

    setSubmitting(true);
    const paymentUrl = buildPaymentUrl({
      orderNo: makeOrderNumber(),
      amount: total,
      currency: STORE.currency,
      email: form.email.trim(),
      title: lineItems[0].product.name,
      origin: window.location.origin,
    });
    window.location.assign(paymentUrl);
  };

  return (
    <main className="main-containerCheck">
      <div className="check-custom-checkout-page">
        <div className="left">
          <div className="check-order-info">
            <h1>Checkout</h1>
            <form onSubmit={handlePayNow} noValidate>
              <fieldset>
                <legend>Contact</legend>
                <div className="form-group">
                  <label htmlFor="email">Email</label>
                  <input type="email" id="email" name="email" autoComplete="email" value={form.email} onChange={updateField} aria-describedby={errors.email ? "email-error" : undefined} />
                  {errors.email && <p className="error" id="email-error">{errors.email}</p>}
                </div>
              </fieldset>

              <fieldset>
                <legend>Delivery address</legend>
                <div className="form-group">
                  <label htmlFor="country">Country / region</label>
                  <select id="country" name="country" value={form.country} onChange={updateField}>
                    {checkoutCountries.map(({ code, name }) => <option key={code} value={code}>{name}</option>)}
                  </select>
                  {errors.country && <p className="error">{errors.country}</p>}
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="sr-only" htmlFor="firstName">First name</label>
                    <input id="firstName" name="firstName" autoComplete="given-name" value={form.firstName} onChange={updateField} placeholder="First name *" />
                    {errors.firstName && <p className="error">{errors.firstName}</p>}
                  </div>
                  <div className="form-group">
                    <label className="sr-only" htmlFor="lastName">Last name</label>
                    <input id="lastName" name="lastName" autoComplete="family-name" value={form.lastName} onChange={updateField} placeholder="Last name *" />
                    {errors.lastName && <p className="error">{errors.lastName}</p>}
                  </div>
                </div>
                <div className="form-group">
                  <label className="sr-only" htmlFor="address">Street address</label>
                  <input id="address" name="address" autoComplete="address-line1" value={form.address} onChange={updateField} placeholder="Street address *" />
                  {errors.address && <p className="error">{errors.address}</p>}
                </div>
                <div className="form-group">
                  <label className="sr-only" htmlFor="address2">Apartment or unit</label>
                  <input id="address2" name="address2" autoComplete="address-line2" value={form.address2} onChange={updateField} placeholder="Apartment, suite, etc. (optional)" />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="sr-only" htmlFor="city">City</label>
                    <input id="city" name="city" autoComplete="address-level2" value={form.city} onChange={updateField} placeholder="City *" />
                    {errors.city && <p className="error">{errors.city}</p>}
                  </div>
                  <div className="form-group">
                    <label className="sr-only" htmlFor="region">State or region</label>
                    <input id="region" name="region" autoComplete="address-level1" value={form.region} onChange={updateField} placeholder="State / region (optional)" />
                  </div>
                </div>
                <div className="form-group">
                    <label className="sr-only" htmlFor="postalCode">Postal code</label>
                    <input id="postalCode" name="postalCode" autoComplete="postal-code" value={form.postalCode} onChange={updateField} placeholder={form.country === "HK" ? "Postal code (optional)" : "Postal code *"} />
                  {errors.postalCode && <p className="error">{errors.postalCode}</p>}
                </div>
              </fieldset>

              <div className="hosted-payment-note">
                <strong>Secure hosted payment</strong>
                <p>Pay Now transfers you to the provider. This form handles no card credential.</p>
              </div>
              {errors.cart && <p className="error cart-error">{errors.cart}</p>}
              <button className="check-checkout-button" type="submit" disabled={submitting || !lineItems.length}>{submitting ? "Redirecting…" : `Pay Now · ${formatMoney(total)}`}</button>
              <p className="checkout-policy-links"><Link to="/shipping">Shipping</Link> · <Link to="/returns">Returns</Link> · <Link to="/privacy">Privacy</Link></p>
            </form>
          </div>
        </div>

        <aside className="right" aria-label="Order summary">
          <div className="check-order-summary">
            <h2>Order summary</h2>
            {lineItems.map((item) => (
              <div key={`${item.id}-${item.size}-${item.color}`} className="check-cart-item">
                <img src={item.product.image} alt="" />
                <div className="pro--qty"><div className="qty-inner">{item.quantity}</div></div>
                <div className="check-item-details">
                  <h3>{item.product.name}</h3>
                  {item.size && <p>Size: {item.size}</p>}
                  {item.color && <p>Colour: {item.color}</p>}
                </div>
                <p className="cal-single">{formatMoney(Number(item.product.price) * item.quantity)}</p>
              </div>
            ))}
            <div className="check-summary-details">
              <div className="summary-line"><span>Subtotal</span><span>{formatMoney(subtotal)}</span></div>
              <div className="summary-line"><span>Shipping</span><span>{formatMoney(STORE.shippingFee)}</span></div>
              <div className="summary-line summary-total"><span>Total</span><span>{formatMoney(total)} {STORE.currency}</span></div>
            </div>
          </div>
        </aside>
      </div>
    </main>
  );
}

export default CheckOut;
